import { useCallback, useEffect, useState } from 'react'
import useApi from './useApi'

/**
hook for returning a location string to { lat, lng } for map centering
 *
 * @param {{ location: string | null | undefined, enabled: boolean }}
 * @returns {{ center: { lat: number, lng: number } | null, loading: boolean, error: Error | null }}
 */
export default function useProfileMapCenter({ location, enabled = true }) {
  const { locationAutocomplete, locationPlaceDetails } = useApi()
  const [center, setCenter] = useState(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)

  const extractCenter = useCallback(data => {
    if (!data) return null
    const lat = data.latitude ?? data.lat
    const lng = data.longitude ?? data.lng
    if (typeof lat === 'number' && typeof lng === 'number') return { lat, lng }
    const loc = data.geometry?.location
    if (loc && typeof loc.lat === 'function' && typeof loc.lng === 'function') {
      return { lat: loc.lat(), lng: loc.lng() }
    }
    if (loc && typeof loc.lat === 'number' && typeof loc.lng === 'number') {
      return { lat: loc.lat, lng: loc.lng }
    }
    return null
  }, [])

  useEffect(() => {
    if (!enabled || !location || typeof location !== 'string' || location.trim().length < 2) {
      setCenter(null)
      setLoading(false)
      setError(null)
      return
    }

    let cancelled = false
    setError(null)
    setLoading(true)
    setCenter(null)

    const run = async () => {
      try {
        const { data: predictions } = await locationAutocomplete({
          input: location.trim(),
        })
        if (cancelled || !predictions?.length || predictions?.error) {
          if (!cancelled) setLoading(false)
          return
        }
        const placeId = predictions[0]?.place_id
        if (!placeId) {
          if (!cancelled) setLoading(false)
          return
        }
        const { data: placeData } = await locationPlaceDetails({ placeId })
        if (cancelled) return
        const nextCenter = extractCenter(placeData)
        if (nextCenter) setCenter(nextCenter)
        setLoading(false)
      } catch (err) {
        if (!cancelled) {
          setError(err)
          setCenter(null)
          setLoading(false)
        }
      }
    }

    run()
    return () => { cancelled = true }
  }, [enabled, location, locationAutocomplete, locationPlaceDetails, extractCenter])

  return { center, loading, error }
}
